import { Button, Chip, Grid, Stack, Typography } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import AppContainer from "../../../components/AppContainer";
import AppLoader from "../../../components/AppLoader";
import NavigationDrawer from "../../../components/NavigationDrawer";
import GlobalTable from "../../../components/Tables/GlobalTable";
import {
  getDonationByOrderId,
  getSingleSupplierOrderById,
  updateSupplierOrderItemStatus,
  updateSupplierOrderStatusInBulk,
} from "../../../services/supplier/orderApiFn";
import {
  calculateProductOrderVatAmount,
  calculateSupplierTotalOrderPrice,
  calculateVat,
  handleOpenSnackBar,
  roundTo2Decimal,
} from "../../../utils";
import { CustomContainer } from "../../../components/Containers";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Label from "../../../components/Label";
import moment from "moment";
import globalStyle from "../../../globalStyle";
import TextInput from "../../../components/TextInputs";
import {
  CURRENCY,
  ORDER_STATUS_TRANSITIONS,
} from "../../../constants/Variables";
import GlobalButton from "../../../components/CustomButton";
import { allIcons } from "../../../assets";
import EcommerceInvoicePDF from "../../../components/InvoiceTemplate/EcommercePdf";
import { PDFDownloadLink } from "@react-pdf/renderer";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

const headerData = [
  "name",
  "variant",
  "quantity",
  "price",
  "profitMargin",
  "discount",
  "finalPrice",
  "vat",
  "vatAmount",
  "amountAfterVat",
  "status",
];

const tableRows = [
  "product_name",
  "order_product_variant",
  "order_product_quantity",
  "order_product_unit_price",
  "order_product_profit_margin",
  "order_product_discount",
  "order_product_final_price",
  "order_product_vat",
  "order_product_vat_amount",
  "order_product_amount_after_vat",
  "product_status",
];

const useStyle = {
  btnStyle: {
    height: 40,
    width: { xs: "100%", md: 120 },
  },
};

const SupplierSalesDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [allOrders, setAllOrders] = useState();
  const [userInfo, setUserInfo] = useState(null);
  const [userAddress, setUserAddress] = useState();
  const [donationDetails, setDonationDetails] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [pendingStatusChanges, setPendingStatusChanges] = useState({});
  const userData = useSelector((state) => state.userData.user);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const fetchOrderDetail = async () => {
    try {
      setIsLoading(true);
      const response = await getSingleSupplierOrderById(userData._id, id);
      if (response?.data.status === "success") {
        const data = response?.data?.data;

        let detail = data;
        let orders = data?.order;
        let userInfo = data?.userId;
        let userAddress = data?.address;

        const modifiedOrder = orders.map((item) => ({
          ...item,
          savedOrderStatus: item.orderStatus,
        }));

        setOrderDetails(detail);
        setUserInfo(userInfo);
        setUserAddress(userAddress);
        setAllOrders(modifiedOrder);
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDonationByOrderId = async (orderId) => {
    try {
      const response = await getDonationByOrderId(orderId);
      if (response?.data.status === "success") {
        console.log(response?.data?.data,"Sdasdjaksdjksajdksa");
        setDonationDetails(response?.data?.data);
      } else {
        console.error(response?.data?.message);
      }
    } catch (error) {
      console.error(error?.message);
    }
  };

  useEffect(() => {
    if (userData._id && id) {
      fetchOrderDetail();
      fetchDonationByOrderId(id);
    }
  }, [userData._id, id]);

  const handleChangeOrderStatus = async (e, row) => {
    const savedStatus = row?.savedOrderStatus ?? row?.orderStatus;

    if (
      savedStatus !== e &&
      !ORDER_STATUS_TRANSITIONS[savedStatus]?.includes(e)
    ) {
      handleOpenSnackBar(
        "error",
        `Cannot change status from ${savedStatus} to ${e}`,
      );
      return;
    }

    if (isBulkMode) {
      setPendingStatusChanges((prev) => {
        const updated = { ...prev };

        if (e === savedStatus) {
          // Back to original status - no real change, drop it from pending list
          delete updated[row._id];
        } else {
          updated[row._id] = e;
        }

        return updated;
      });
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateSupplierOrderItemStatus(
        orderDetails._id,
        row._id,
        { orderStatus: e },
      );
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        fetchOrderDetail();
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleBulkUpdateSubmit = async () => {
    const updates = Object.entries(pendingStatusChanges).map(
      ([itemId, orderStatus]) => ({
        itemId,
        orderStatus,
      }),
    );

    if (updates.length === 0) {
      handleOpenSnackBar("info", "Please select at least one status change");
      return;
    }

    try {
      setIsLoading(true);
      const response = await updateSupplierOrderStatusInBulk(orderDetails._id, {
        updates,
      });
      if (response?.data.status === "success") {
        handleOpenSnackBar("success", response?.data.message);
        setPendingStatusChanges({});
        fetchOrderDetail();
      } else {
        handleOpenSnackBar("error", response?.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const totalVatAmount = useMemo(
    () => calculateProductOrderVatAmount(allOrders),
    [allOrders],
  );

  const discountVat = useMemo(
    () =>
      roundTo2Decimal(
        calculateVat(
          orderDetails?.discount?.amount || 0,
          orderDetails?.vat || 0,
        ),
      ),
    [orderDetails?.discount?.amount, orderDetails?.vat],
  );

  const supplierTotalOrderPrice = useMemo(
    () => calculateSupplierTotalOrderPrice(allOrders) + totalVatAmount,
    [allOrders, orderDetails],
  );

  const hasAnyProductInContinueStage = allOrders?.some(
    (item) =>
      item.orderStatus === "pending" ||
      item.orderStatus === "processing" ||
      item.orderStatus === "shipped",
  );

  const renderRightAction = () => {
    return (
      <Stack direction="row" justifyContent={"center"}>
        <PDFDownloadLink
          document={
            <EcommerceInvoicePDF
              supplierOrders={allOrders}
              invoiceData={orderDetails}
              currentLanguage={currentLanguage}
              donationDetails={donationDetails}
            />
          }
          fileName={`invoice-${orderDetails?.orderId}.pdf`}
        >
          {({ blob, url, loading, error }) => (
            <Button
              variant="contained"
              disabled={loading}
              sx={{
                width: "auto",
                height: "30px",
                borderRadius: "6px",
              }}
              startIcon={<FileDownloadIcon />}
            >
              {loading
                ? t("generating", { ns: "common" })
                : t("invoice", { ns: "common" })}
            </Button>
          )}
        </PDFDownloadLink>
      </Stack>
    );
  };

  return (
    <NavigationDrawer>
      <AppLoader isVisible={isLoading} />
      <CustomContainer
        primaryHeading={t("saleDetail")}
        secondaryHeading={t("saleDetailDescription")}
        onBackButton={() => navigate(-1)}
        customHeaderActions={
          !hasAnyProductInContinueStage ? renderRightAction() : null
        }
      >
        <Grid container spacing={2}>
          {orderDetails?.isPackageOrder && (
            <Grid item xs={12}>
              <Chip
                icon={<Inventory2Icon />}
                label={t(
                  orderDetails?.packageType === "custom"
                    ? "customPackageOrder"
                    : "basePackageOrder",
                )}
                color="primary"
              />
            </Grid>
          )}

          {/* {orderDetails?.discount?.type === "slab" && (
            <Grid item xs={12}>
              <Chip
                icon={<img src={allIcons.packageIconActive} width={"20px"} />}
                label={t("planOrder")}
                color="primary"
              />
            </Grid>
          )} */}

          {/* {orderDetails?.discount?.type === "promo" && (
            <Grid item xs={12}>
              <Chip
                icon={<img src={allIcons.packageIconActive} width={"20px"} />}
                label={t("promoApplied")}
                color="primary"
              />
            </Grid>
          )} */}

          {/* {orderDetails?.planId && (
            <Grid item xs={12}>
              <Chip
                icon={<LocalShippingIcon />}
                label={t("planOrder")}
                color="primary"
              />
            </Grid>
          )} */}

          {/* {orderDetails?.event?.eventId && (
            <Grid item xs={12}>
              <Chip label={t("giftRegistryOrder")} color="secondary" />
            </Grid>
          )} */}

          {/*
          <Grid item md={12}>
            <Typography variant="h3" fontSize={20} fontWeight={600}>
              {t("clientInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("clientFullName")} required />
            <TextInput
              readonly
              placeholder={t("username")}
              styleProps={globalStyle.textField}
              value={`${userInfo?.firstname} ${userInfo?.lastname}` || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("email")} required />
            <TextInput
              readonly
              placeholder={t("userEmail")}
              styleProps={globalStyle.textField}
              value={userInfo?.email || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("contactNumber")} required />
            <TextInput
              readonly
              placeholder={t("contactNumber")}
              styleProps={globalStyle.textField}
              value={userInfo?.contact || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("state")} required />
            <TextInput
              readonly
              placeholder={t("state")}
              styleProps={globalStyle.textField}
              value={userAddress?.state || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("city")} required />
            <TextInput
              readonly
              placeholder={t("city")}
              styleProps={globalStyle.textField}
              value={userAddress?.city || ""}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("zipCode")} required />
            <TextInput
              readonly
              placeholder={t("zipCode")}
              styleProps={globalStyle.textField}
              value={userAddress?.zipCode || ""}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <Label labelText={t("destinationAddress")} required />
            <TextInput
              readonly
              placeholder={t("activityDescription")}
              styleProps={globalStyle.textField}
              value={`${userAddress?.address}` || ""}
            />
          </Grid>
          */}
        </Grid>

        <Grid container spacing={2} mt={2}>
          <Grid item md={12}>
            <Typography variant="h3" fontSize={20} fontWeight={600}>
              {t("orderInformation")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("createdAt")} required />
            <TextInput
              readonly
              placeholder={t("createdAt")}
              styleProps={globalStyle.textField}
              value={`${moment(orderDetails?.createdAt).format("MM/DD/YYYY")}`}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("orderId")} required />
            <TextInput
              readonly
              placeholder={t("orderId")}
              styleProps={globalStyle.textField}
              value={orderDetails?.orderId}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Label labelText={t("totalBill")} required />
            <TextInput
              readonly
              placeholder={t("totalBill")}
              styleProps={globalStyle.textField}
              // value={roundTo2Decimal(supplierTotalOrderPrice-) || 0}
              value={
                roundTo2Decimal(
                  supplierTotalOrderPrice -
                    (orderDetails?.discount?.amount || 0) -
                    discountVat,
                ) || 0
              }
              InputEndIcon={CURRENCY}
            />
          </Grid>
        </Grid>

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          mb={2}
        >
          <Typography variant="h3" fontSize={20} fontWeight={600} mt={3} mb={2}>
            {t("allOrders")}
          </Typography>
          <Stack direction="row" gap={2} alignItems="center">
            <GlobalButton
              variant={"outlined"}
              btnText={
                isBulkMode ? "switchToSingleUpdate" : "switchToBulkUpdate"
              }
              handleClickBtn={() => {
                setIsBulkMode((prev) => !prev);
                setPendingStatusChanges({});
              }}
              styleProps={{
                width: "auto",
              }}
            />
            {isBulkMode && (
              <GlobalButton
                variant={"contained"}
                disabled={Object.keys(pendingStatusChanges).length === 0}
                btnText={`${t("updateAll")} (${Object.keys(pendingStatusChanges).length})`}
                handleClickBtn={handleBulkUpdateSubmit}
                styleProps={{
                  width: "auto",
                }}
              />
            )}
          </Stack>
        </Stack>

        <GlobalTable
          minWidth={1800}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allOrders}
          handleChangeOrderStatus={handleChangeOrderStatus}
          isBulkMode={isBulkMode}
          pendingStatusChanges={pendingStatusChanges}
        />
      </CustomContainer>
    </NavigationDrawer>
  );
};

export default SupplierSalesDetail;
